import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { QuoteTemplate, CreateQuoteTemplateDto, UpdateQuoteTemplateDto } from '../../models/forwarding/quote-template.model';

@Injectable({
  providedIn: 'root'
})
export class QuoteTemplateService {
  private apiUrl = `${environment.apiUrl}/forwarding/quote-templates`;
  private templatesSubject = new BehaviorSubject<QuoteTemplate[]>([]);
  public templates$ = this.templatesSubject.asObservable();

  constructor(private http: HttpClient) {
    this.loadTemplates();
  }

  private loadTemplates(): void {
    console.log('[QuoteTemplateService] Loading quote templates from:', this.apiUrl);
    this.http.get<QuoteTemplate[]>(this.apiUrl).subscribe({
      next: (templates) => {
        console.log('[QuoteTemplateService] Successfully loaded templates from API:', templates.length);
        this.templatesSubject.next(templates);
      },
      error: (error) => {
        console.error('[QuoteTemplateService] Error loading templates from API:', error);
        this.templatesSubject.next([]);
      }
    });
  }

  getTemplates(): Observable<QuoteTemplate[]> {
    return this.templates$;
  }

  getById(id: number): Observable<QuoteTemplate> {
    return this.http.get<QuoteTemplate>(`${this.apiUrl}/${id}`);
  }

  getDefaultTemplate(shipmentMode?: string, containerLoadType?: string, incotermScope?: string): Observable<QuoteTemplate> {
    let params = new HttpParams();
    if (shipmentMode) params = params.set('shipmentMode', shipmentMode);
    if (containerLoadType) params = params.set('containerLoadType', containerLoadType);
    if (incotermScope) params = params.set('incotermScope', incotermScope);

    return this.http.get<QuoteTemplate>(`${this.apiUrl}/default`, { params });
  }

  create(dto: CreateQuoteTemplateDto): Observable<QuoteTemplate> {
    return this.http.post<QuoteTemplate>(this.apiUrl, dto).pipe(
      tap(() => {
        console.log('[QuoteTemplateService] Template created, reloading');
        this.loadTemplates();
      })
    );
  }

  update(id: number, dto: UpdateQuoteTemplateDto): Observable<QuoteTemplate> {
    return this.http.put<QuoteTemplate>(`${this.apiUrl}/${id}`, dto).pipe(
      tap(() => {
        console.log('[QuoteTemplateService] Template updated, reloading');
        this.loadTemplates();
      })
    );
  }

  setDefault(id: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/${id}/set-default`, {}).pipe(
      tap(() => {
        console.log('[QuoteTemplateService] Default template set, reloading');
        this.loadTemplates();
      })
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      tap(() => {
        console.log('[QuoteTemplateService] Template deleted, reloading');
        this.loadTemplates();
      })
    );
  }

  refresh(): void {
    this.loadTemplates();
  }
}
